import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Helper function to convert blob URL to base64
const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

// Helper function to convert image URL to base64
const imageUrlToBase64 = async (url) => {
    try {
        if (url.startsWith('data:')) {
            return url;
        }

        if (url.startsWith('blob:')) {
            const response = await fetch(url);
            const blob = await response.blob();
            return await blobToBase64(blob);
        }

        const response = await fetch(url);
        const blob = await response.blob();
        return await blobToBase64(blob);
    } catch (error) {
        console.warn('Failed to convert image to base64:', url, error);
        return url;
    }
};

export const generatePDF = async (elementId) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    try {
        // Clone the element to avoid modifying the original
        const clone = element.cloneNode(true);

        // Map original images to clone images to preserve computed dimensions
        const originalImages = Array.from(element.getElementsByTagName('img'));
        const cloneImages = Array.from(clone.getElementsByTagName('img'));

        originalImages.forEach((origImg, index) => {
            if (cloneImages[index]) {
                const rect = origImg.getBoundingClientRect();
                cloneImages[index].style.width = `${rect.width}px`;
                cloneImages[index].style.height = `${rect.height}px`;
                cloneImages[index].style.maxWidth = 'none';
                cloneImages[index].style.maxHeight = 'none';

                // Also copy the PARENT's styling to ensure container alignment
                const origParent = origImg.parentElement;
                const cloneParent = cloneImages[index].parentElement;
                if (origParent && cloneParent) {
                    const parentStyle = window.getComputedStyle(origParent);
                    cloneParent.style.backgroundColor = parentStyle.backgroundColor;
                    cloneParent.style.padding = parentStyle.padding;
                    cloneParent.style.borderRadius = parentStyle.borderRadius;
                    // Force white background visibility
                    if (origImg.alt === 'Business Logo') {
                        cloneParent.style.zIndex = '50';
                        cloneParent.style.position = 'relative';
                        cloneParent.style.backgroundColor = '#ffffff'; // Enforce white
                    }
                }
            }
        });

        // Convert all images to Base64
        const imagePromises = cloneImages.map(async (img) => {
            if (img.src && (img.src.startsWith('blob:') || img.src.startsWith('http'))) {
                try {
                    const base64 = await imageUrlToBase64(img.src);
                    img.src = base64;
                } catch (error) {
                    console.warn('Failed to convert image:', error);
                }
            }
        });

        await Promise.all(imagePromises);

        // Temporarily add clone to document for rendering
        // Use fixed to ensure it's at top-left and distinct from document flow
        clone.style.position = 'fixed';
        clone.style.top = '0';
        clone.style.left = '0';
        clone.style.zIndex = '-9999';
        clone.style.width = element.offsetWidth + 'px';
        clone.style.margin = '0';
        clone.style.padding = '0';

        // Ensure the clone has a white background to prevent transparency issues
        clone.style.backgroundColor = '#ffffff';

        document.body.appendChild(clone);

        // Find the Business Logo specifically and FORCE it to be visible
        // We removed the manual drawing logic as it was misaligning.
        // Instead, we will rely on css enforcement.
        const businessLogo = Array.from(clone.getElementsByTagName('img')).find(img => img.alt === 'Business Logo');
        if (businessLogo) {
            businessLogo.style.opacity = '1';
            businessLogo.style.visibility = 'visible';
            businessLogo.style.display = 'block';
            // Ensure parent is also visible
            if (businessLogo.parentElement) {
                businessLogo.parentElement.style.opacity = '1';
                businessLogo.parentElement.style.visibility = 'visible';
            }
        }

        // Wait for images to be "ready" 
        await new Promise(resolve => setTimeout(resolve, 1000)); // Increased delay slightly

        // Standard html2canvas call
        const canvas = await html2canvas(clone, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: true,
            backgroundColor: '#ffffff',
            imageTimeout: 5000 // Give it time
        });

        document.body.removeChild(clone);

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        const pdfWidth = 210;
        const pdfHeight = 297;

        const imgWidth = pdfWidth;
        let imgHeight = (canvas.height * pdfWidth) / canvas.width;

        // If content height exceeds A4 height, scale ONLY the height down to fit one page
        // This prevents white bars on the sides while ensuring everything fits on one page
        if (imgHeight > pdfHeight) {
            imgHeight = pdfHeight;
        }

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

        pdf.save(`invoice_${Date.now()}.pdf`);

    } catch (error) {
        console.error("PDF Generation Error:", error);
        alert("Failed to generate PDF. Please try again.");
    }
};
